import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import type { HistoryItem, UserTier } from '../types';
import { getHistory, createCheckoutSession } from '../services/api';
import { CopyButton } from './CopyButton';

interface RewriteHistoryPanelProps {
  tier: UserTier;
  onClose: () => void;
  onEditOriginal: (item: HistoryItem) => void;
}

// Mirrors the template badge styling used by HistoryCard.tsx.
const templateLabels: Record<string, string> = {
  status_update: 'Status update',
  risk_escalation: 'Risk / escalation',
  metrics_translation: 'Metrics translation',
  executive_summary: 'Executive summary',
  tone_adjustment: 'Slack / email tone',
};

const TEMPLATE_COLORS: Record<
  string,
  { background: string; color: string; borderColor: string }
> = {
  status_update: { background: '#1e3a5f', color: '#93C5FD', borderColor: '#2E5494' },
  risk_escalation: { background: '#2d1a00', color: '#FCD34D', borderColor: '#92400E' },
  metrics_translation: { background: '#14290a', color: '#86EFAC', borderColor: '#166534' },
  executive_summary: { background: '#1e1040', color: '#C4B5FD', borderColor: '#5B21B6' },
  tone_adjustment: { background: '#242424', color: '#9CA3AF', borderColor: '#3a3a3a' },
};

const defaultColor = { background: '#242424', color: '#9CA3AF', borderColor: '#3a3a3a' };

/** Human-readable "time ago" for a rewrite's created_at ISO string. */
function formatRelativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';

  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;

  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;

  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  return `${days} days ago`;
}

const sectionLabelStyle: React.CSSProperties = {
  fontSize: '11px',
  fontWeight: 500,
  color: 'var(--text-muted)',
  margin: 0,
};

const outputBoxStyle: React.CSSProperties = {
  padding: '10px',
  background: 'var(--bg-secondary)',
  border: '0.5px solid var(--border)',
  borderRadius: '8px',
  minHeight: '72px',
};

export function RewriteHistoryPanel({
  tier,
  onClose,
  onEditOriginal,
}: RewriteHistoryPanelProps) {
  const isPro = tier === 'pro';
  const [items, setItems] = useState<HistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(isPro);
  const [hasError, setHasError] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  // Free users see the upgrade CTA — never call the Pro-only endpoint.
  useEffect(() => {
    if (!isPro) return;
    let cancelled = false;
    getHistory(5)
      .then((res) => {
        if (cancelled) return;
        setItems(res.items);
        setIsLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setHasError(true);
        setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isPro]);

  async function handleUpgrade() {
    if (checkoutLoading) return;
    setCheckoutLoading(true);
    try {
      const url = await createCheckoutSession();
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      // Best-effort — the button recovers to its idle label on failure.
    } finally {
      setCheckoutLoading(false);
    }
  }

  return (
    <>
      <style>{`
        @keyframes myiq-rwpanel-in {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        @keyframes myiq-rwshimmer {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 0.7; }
        }
        .myiq-rwhistory-panel {
          position: absolute;
          top: 0;
          right: 0;
          bottom: 0;
          width: 320px;
          z-index: 30;
          display: flex;
          flex-direction: column;
          background: var(--bg-card);
          border-left: 1px solid var(--border);
          box-shadow: -8px 0 24px rgba(0, 0, 0, 0.28);
          animation: myiq-rwpanel-in 0.22s ease-out;
        }
        @media (max-width: 640px) {
          .myiq-rwhistory-panel {
            position: fixed;
            width: 100%;
          }
        }
        .myiq-rwhistory-item {
          width: 100%;
          text-align: left;
          background: transparent;
          border: none;
          border-left: 3px solid transparent;
          padding: 10px 16px;
          cursor: pointer;
          transition: background 0.15s;
        }
        .myiq-rwhistory-item:hover {
          background: var(--bg-elevated);
        }
        .myiq-rwhistory-item.active {
          border-left: 3px solid var(--accent);
          background: var(--accent-bg);
        }
      `}</style>

      <aside className="myiq-rwhistory-panel" aria-label="Rewrite history">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px',
            borderBottom: '1px solid var(--border)',
          }}
        >
          <h2
            style={{
              margin: 0,
              fontFamily: "'Outfit', sans-serif",
              fontSize: '15px',
              fontWeight: 700,
              color: 'var(--text-primary)',
            }}
          >
            Rewrite history
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              display: 'flex',
              padding: '4px',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--text-muted)',
            }}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', paddingBottom: '8px' }}>
          {!isPro ? (
            <div style={{ padding: '32px 20px', textAlign: 'center' }}>
              <p
                style={{
                  fontFamily: "'Outfit', sans-serif",
                  fontSize: '15px',
                  fontWeight: 700,
                  color: 'var(--text-primary)',
                  margin: '0 0 8px',
                }}
              >
                History is a Pro feature
              </p>
              <p
                style={{
                  fontSize: '13px',
                  color: 'var(--text-muted)',
                  lineHeight: 1.5,
                  margin: '0 0 20px',
                }}
              >
                Upgrade to Pro to access your past rewrites
              </p>
              <button
                onClick={handleUpgrade}
                disabled={checkoutLoading}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  borderRadius: '8px',
                  border: 'none',
                  background: 'var(--accent)',
                  color: 'var(--btn-primary-text)',
                  fontSize: '13px',
                  fontWeight: 600,
                  fontFamily: "'Outfit', sans-serif",
                  cursor: checkoutLoading ? 'wait' : 'pointer',
                }}
              >
                {checkoutLoading ? 'Opening…' : 'Upgrade to Pro — $49/month'}
              </button>
            </div>
          ) : isLoading ? (
            [0, 1, 2, 3].map((i) => (
              <div
                key={i}
                data-testid="rewrite-skeleton"
                style={{
                  height: '14px',
                  margin: '14px 16px',
                  borderRadius: '4px',
                  background: 'var(--bg-elevated)',
                  animation: 'myiq-rwshimmer 1.4s ease-in-out infinite',
                }}
              />
            ))
          ) : hasError ? (
            <p
              style={{
                margin: '24px 16px',
                fontSize: '13px',
                color: 'var(--error-text)',
                textAlign: 'center',
              }}
            >
              Couldn't load your history. Please try again.
            </p>
          ) : items.length === 0 ? (
            <p
              style={{
                margin: '24px 16px',
                fontSize: '13px',
                color: 'var(--text-muted)',
                textAlign: 'center',
              }}
            >
              No rewrites yet
            </p>
          ) : (
            items.map((item) => {
              const isExpanded = expandedId === item.rewrite_id;
              const color = TEMPLATE_COLORS[item.template] || defaultColor;
              const title = item.raw_input.slice(0, 60);
              return (
                <div key={item.rewrite_id}>
                  <button
                    className={
                      isExpanded
                        ? 'myiq-rwhistory-item active'
                        : 'myiq-rwhistory-item'
                    }
                    onClick={() =>
                      setExpandedId(isExpanded ? null : item.rewrite_id)
                    }
                  >
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '3px 8px',
                        fontSize: '10px',
                        fontWeight: 500,
                        borderRadius: '9999px',
                        border: `1px solid ${color.borderColor}`,
                        background: color.background,
                        color: color.color,
                        marginBottom: '6px',
                      }}
                    >
                      {templateLabels[item.template] || item.template}
                    </span>
                    <div
                      style={{
                        fontSize: '13px',
                        fontWeight: 500,
                        color: 'var(--text-primary)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {title || 'Untitled rewrite'}
                    </div>
                    <div
                      style={{
                        marginTop: '3px',
                        fontSize: '11px',
                        color: 'var(--text-muted)',
                      }}
                    >
                      {formatRelativeTime(item.created_at)}
                    </div>
                  </button>

                  {isExpanded && (
                    <div style={{ padding: '4px 16px 16px' }}>
                      <p style={{ ...sectionLabelStyle, marginBottom: '6px' }}>
                        Original input
                      </p>
                      <p
                        style={{
                          fontSize: '13px',
                          color: 'var(--text-secondary)',
                          background: 'var(--bg-secondary)',
                          padding: '10px',
                          borderRadius: '8px',
                          border: '0.5px solid var(--border)',
                          lineHeight: 1.6,
                          margin: 0,
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {item.raw_input}
                      </p>

                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          gap: '8px',
                          marginTop: '12px',
                        }}
                      >
                        <div>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              marginBottom: '6px',
                            }}
                          >
                            <p style={sectionLabelStyle}>Concise</p>
                            <CopyButton text={item.concise} />
                          </div>
                          <div
                            className="markdown-content"
                            style={outputBoxStyle}
                          >
                            <ReactMarkdown>{item.concise}</ReactMarkdown>
                          </div>
                        </div>
                        <div>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              marginBottom: '6px',
                            }}
                          >
                            <p style={sectionLabelStyle}>Detailed</p>
                            <CopyButton text={item.detailed} />
                          </div>
                          <div
                            className="markdown-content"
                            style={outputBoxStyle}
                          >
                            <ReactMarkdown>{item.detailed}</ReactMarkdown>
                          </div>
                        </div>
                      </div>

                      <button
                        onClick={() => onEditOriginal(item)}
                        style={{
                          width: '100%',
                          marginTop: '12px',
                          padding: '8px 12px',
                          borderRadius: '8px',
                          fontSize: '13px',
                          fontWeight: 600,
                          color: 'var(--accent)',
                          background: 'var(--accent-bg)',
                          border: '1px solid var(--border)',
                          cursor: 'pointer',
                        }}
                      >
                        Edit original
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </aside>
    </>
  );
}
